import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X } from "lucide-react";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { equipment } from "@/data/site";

export default function Strumentazione() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <main data-testid="strumentazione-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="pt-36 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Strumentazione</SectionLabel>
            <h1 className="font-serif font-medium mt-5 text-5xl md:text-7xl tracking-tight leading-[1.02] text-slate-900">
              La nostra<br />
              <em className="not-italic text-amber-800">dotazione tecnica.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg text-slate-600 leading-relaxed">
              Lo studio dispone delle più innovative strumentazioni e
              tecnologie sia per il rilievo — Stazione totale, GNSS, Laser
              Scanner, Drone — sia per l'elaborazione dei dati, garantendo i
              migliori risultati per il cliente.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES */}
      {equipment.map((cat, idx) => (
        <section
          key={cat.category}
          data-testid={`equipment-category-${idx}`}
          className={`py-20 lg:py-28 ${idx % 2 === 0 ? "bg-slate-50" : "bg-white"}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-8 mb-12">
              <div className="lg:col-span-5">
                <Reveal>
                  <SectionLabel>Categoria {String(idx + 1).padStart(2, "0")}</SectionLabel>
                  <SectionTitle className="mt-4">{cat.category}</SectionTitle>
                </Reveal>
              </div>
              <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                <Reveal delay={0.1}>
                  <p className="text-base text-slate-600 leading-relaxed">
                    {cat.description}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="space-y-16">
              {cat.items.map((item, i) => (
                <Reveal key={item.name} delay={i * 0.05}>
                  <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-4">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-amber-800 font-semibold">
                        Modello
                      </div>
                      <div className="font-serif text-3xl mt-3 text-slate-900 leading-tight">
                        {item.name}
                      </div>
                      {item.sub && (
                        <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                          {item.sub}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-3">
                      {item.photos.map((p, j) => (
                        <button
                          key={j}
                          type="button"
                          onClick={() => setLightbox({ src: p, name: item.name })}
                          data-testid={`equipment-photo-${item.name.replace(/\s/g, "-")}-${j}`}
                          className="img-zoom aspect-[4/3] border border-slate-200 hover:border-slate-900 transition-colors bg-white"
                        >
                          <img
                            src={p}
                            alt={`${item.name} ${j + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* NOTE */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="font-serif text-3xl md:text-4xl leading-snug">
              "La competenza e l'esperienza maturata negli anni, unitamente
              alle proprietà della strumentazione disponibile, rappresentano
              garanzia di efficienza e di affidabilità nei confronti del
              cliente."
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm text-slate-400">
              Altra strumentazione reperibile a seconda delle specifiche
              esigenze.
            </p>
            <Link
              to="/contatti"
              data-testid="strumentazione-cta-contact"
              className="mt-10 inline-flex items-center gap-2 bg-amber-300 text-slate-900 px-8 py-4 rounded-full font-medium hover:bg-amber-200 transition-colors"
            >
              Richiedi informazioni <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="equipment-lightbox"
          className="fixed inset-0 z-[60] bg-slate-950/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.name}
              className="w-full max-h-[80vh] object-contain"
            />
            <div className="text-center text-white mt-4 font-serif text-xl">{lightbox.name}</div>
          </div>
        </div>
      )}
    </main>
  );
}
